import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    OnChanges,
    KeyValueDiffers,
    KeyValueDiffer,
    ChangeDetectorRef,
    ViewChild,
    ElementRef,
    EventEmitter,
    Output,
} from '@angular/core';

import { filter, Observable, Subscription, take, tap, debounce, of, timer, distinctUntilChanged } from 'rxjs';
import { MenuItem } from 'primeng/api';

import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import type { BlockModel } from '../../models/block.model';
import { BlockMoveDirectionEnum } from '../../enums/direction.enum';
import { ClickOutsideService } from '../../services/click-outside.service';
import { focusManagementHelper } from '../../../Misc/helpers/focus/focus-management.helper';
import { ButtonTypesEnum, FocusedMoveButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CommandParams, KebabMenuComponent, PanelPositionEnum } from '../../../Forms/components/KebabMenu/kebab-menu.component';
import { BlockTypeGroupModel } from '../../models/block-type-group.model';
import { LayoutTypesEnum } from '../../../Layout/enums/layout-types.enum';
import { FocusedBlockInfo } from '../../models/focused-block-info.model';
import { BriefingBlockTypeEnum } from '../../../../Monitor/Briefings/enums/briefing-block-type.enum';
import { BriefingDateRangeTypeEnum, RECURRENCE_DATE_RANGE_PLACEHOLDER } from '../../../../Monitor/Briefings/enums/briefing-date-range-type.enum';
import { BlockDisplayTypeEnum } from '../../enums/block-display-type.enum';
import { BlockDisplayElementChangedEvent } from '../../models/block-display-element-changed-event.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';

@Component({
    selector: 'agpr-block',
    templateUrl: './block.component.html',
    styleUrls: ['./block.component.scss'],
    standalone: false
})
export class BlockComponent implements OnInit, OnDestroy, OnChanges {

    static renderCount = 0;

    @Input() block: BlockModel;
    @Input() showMoveButtons: boolean = true;
    @Input() preview: boolean = false;
    @Input()
    set blockPosition(position: string) {
        if (position) {
            this._blockPosition = position;
            this.blockPositionBottom = position.replace(/\d$/, (match) => +match + 1 + '');
            this.restoreFocus(this.moveInfo);
            this.assignId();
        }
    }
    get blockPosition() {
        return this._blockPosition;
    }
    @Input() isInLayoutBlock: boolean = false;
    @Input() isInCuratorBlock: boolean = false;
    @Input() hideTooltip: boolean = false;
    @Input() inlineAdderAdjustment: boolean;
    @Input() actionMenuTextAlignment: string;

    _blockPosition: string;
    blockPositionBottom: string;
    oldBlockPosition: string;
    editing: boolean;
    renderable: boolean;
    id: string;
    dataBlockType: string;
    actionable: boolean = true;
    displayDiffer: KeyValueDiffer<string, any>;
    stylesDiffer: KeyValueDiffer<string, any>;
    generalStyles: any = {};
    addTopExpanded: boolean = false;
    addBottomExpanded: boolean = false;
    areLayoutBlocksAllowed: boolean = false;
    blockTypeGroups: BlockTypeGroupModel[];
    movingOptions: MenuItem[];
    inLayoutBlockMovingOptions: MenuItem[];
    editingOptions: MenuItem[];
    isRightMost: boolean = false;
    moveInfo: FocusedBlockInfo;
    inlineAdderHeight: number;
    optionsTop: MenuItem[];
    optionsBottom: MenuItem[];
    optionsCenter: MenuItem[];

    // Cached moveability state to avoid unnecessary menu rebuilds
    private _cachedMoveState: {
        canMoveUp: boolean;
        canMoveDown: boolean;
        canMoveUpIntoLayout: boolean;
        canMoveUpIntoCurator: boolean;
        canMoveDownIntoLayout: boolean;
        canMoveDownIntoCurator: boolean;
        canMoveLeft: boolean;
        canMoveRight: boolean;
        canDuplicate: boolean;
    } = null;

    protected subscriptions: Subscription[] = [];
    protected twigData: any = {};

    readonly blockAdderId: string = '_blockAdder';
    readonly moveUpButtonClassName: string = 'agpr-block-moveUpButton';
    readonly moveDownButtonClassName: string = 'agpr-block-moveDownButton';
    readonly buttonTypes = ButtonTypesEnum;
    readonly panelPositions = PanelPositionEnum;
    readonly briefingBlockTypeEnum = BriefingBlockTypeEnum;
    readonly blockDisplayTypeEnum = BlockDisplayTypeEnum;

    @Output() outputEvent = new EventEmitter<any>();

    @ViewChild('moveContextMenu') moveContextMenu: KebabMenuComponent;

    @ViewChild('moveButtons') moveButtons: ElementRef;


    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        protected changeDetectorRef: ChangeDetectorRef,
        public clickOutsideService: ClickOutsideService,
        public el: ElementRef
    ) {}

    ngOnInit(): void {
        this.displayDiffer = this.differs.find(this.block.displayElement).create();
        this.stylesDiffer = this.differs.find(this.block.displayElement.styles).create();

        this.assignId();
        this.areLayoutBlocksAllowed = this.pageBuilderService.areLayoutBlocksAllowed();
        if (this.isInLayoutBlock) {
            this.setContextMenuInLayoutBlockMovingOptions();
            this.setContextMenuEditingOptions();
        }

        if (this.isInCuratorBlock) {
            this.setContextMenuInCuratorBlockMovingOptions();
            this.setContextMenuEditingOptions();
        }

        this.setContextMenuMovingOptions();
        this.updateCachedMoveState();
        this.subscriptions.push(
            this.pageBuilderService.getSelectedBlock().subscribe(
                (block) => {
                    this.setEditing(this.block === block);
                }
            ),
            this.pageBuilderService.getBlocks().subscribe(blocks => {
                if (this.hasMoveabilityChanged()) {
                    if (this.isInLayoutBlock) {
                        this.setContextMenuInLayoutBlockMovingOptions();
                        this.setContextMenuEditingOptions();
                    }
                    if (this.isInCuratorBlock) {
                        this.setContextMenuInCuratorBlockMovingOptions();
                        this.setContextMenuEditingOptions();
                    }
                    this.setContextMenuMovingOptions();
                    this.updateCachedMoveState();
                }
            }),
            this.clickOutsideService.getClickOutsideBlock().subscribe((deselectionEvent: boolean) => {
                if (deselectionEvent && this.editing) {
                    this.setEditing(false);
                    this.pageBuilderService.deselectBlock(true);
                }
            }),
            this.pageBuilderService.getBlockTypeGroups().subscribe(blockTypeGroups => {
                this.blockTypeGroups = blockTypeGroups;
                if (this.isInLayoutBlock || this.isInCuratorBlock) {
                    this.setContextMenuEditingOptions();
                }
            }),
            this.pageBuilderService.getFocusedBlockInfo().pipe(filter(x => !!x)).subscribe(moveInfo => {
                this.moveInfo = moveInfo;
                this.restoreFocus(this.moveInfo);
            }),
            this.pageBuilderService.getInlineAdderHeight().subscribe(height => {
                this.inlineAdderHeight = height;
            }),
            this.subscribeToBlockChanges().subscribe()
        );

        this.setBlockAdderOptions();
        this.ngOnChanges();
        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    ngOnChanges(): void {
        this.renderable = this.block.displayElement.canRender();
        this.updateTwigData();
    }

    subscribeToBlockChanges(): Observable<BlockDisplayElementChangedEvent> {
        return this.pageBuilderService.getBlockDisplayElementChanged().pipe(
            debounce((event) => event.block === this.block ? of(null) : timer(300)),
            distinctUntilChanged(),
            tap(event => {
                if (
                    !this.renderable ||
                    event.block === this.block ||
                    (event.stylesOnly && event.block.type.key === this.block.type.key)
                ) {
                    this.ngOnChanges();
                }
            })
        );
    }

    updateTwigData() {
        const data: any = {};
        this.twigData = Object.assign(data, this.block.displayElement, this.block.displayElement.settings);

        if (this.twigData.image && typeof this.twigData === 'object') {
            const { url, altText } = this.twigData.image;
            this.twigData.url = url;
            this.twigData.altText = altText;
        }

        if (this.twigData.headingLevel) {
            this.twigData.titleSettings = {headingLevel: this.twigData.headingLevel};
        }

        // Date blocks rendered via the twig (email) path must receive `dateQuery` in the same
        // backend/API shape the render service serves it server-side, so the local twig preview
        // matches the sent email. The ONLINE path keeps the native model for agpr-date-display.
        if (this.twigData.dateQuery?.type && this.block.displayElement.displayType !== BlockDisplayTypeEnum.ONLINE) {
            // The twig renders a range from `resolvedDateRange` (single-day relatives from `date`),
            // which the backend computes at send time. Resolve it here too so the local preview
            // matches the sent email instead of falling back to the single `data.date`.
            const resolved = DateQueryModel.resolveBriefingDateRange(this.twigData.dateQuery);
            if (resolved?.single) {
                this.twigData.date = resolved.start;
            } else if (resolved) {
                this.twigData.resolvedDateRange = { start: resolved.start, end: resolved.end };
            }
            this.twigData.dateQuery = DateQueryModel.toSearchCriteria(this.twigData.dateQuery);
        }

        // A recurrence-based range has no concrete dates in the draft. Surface the placeholder so
        // the twig can render it in the editor preview; at send time the backend resolves the
        // actual coverage period (into `resolvedDateRange`) and renders that instead.
        if (this.twigData.dateRangeType === BriefingDateRangeTypeEnum.RECURRENCE
            && this.block.displayElement.displayType !== BlockDisplayTypeEnum.ONLINE) {
            this.twigData.recurrenceDateRangePlaceholder = RECURRENCE_DATE_RANGE_PLACEHOLDER;
        }

        // convert /n to <br /> to properly render new line in twig file as company bio uses multi line text area as input
        if (this.twigData.companyBio) {
            this.twigData.companyBio = this.twigData.companyBio.replace(/(?:\r\n|\r|\n)/g, '<br />');
        }
    }

    /**
     * Performs an action on the block
     *
     * @param action A key for a specific action
     */
    onAction(action: string, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        if (!this.block.actionable) {
            return;
        }
        switch (action) {
            case 'edit':
                this.pageBuilderService.selectBlock(this.block);
                this.clickOutsideService.setClassComparisonRegex(this.block.clickOutsideComparisonRegex);
                break;
            case 'delete':
                this.pageBuilderService.removeBlock(this.block, this.blockPosition);
                break;
            case 'duplicate':
                this.pageBuilderService.duplicateBlock(this.block, this.blockPositionBottom);
                break;
        }
        event?.stopPropagation();
    }

    /**
     * Triggered when the move kebab menu is opened.
     * Forces change detection on this block so the kebab menu receives the current movingOptions.
     */
    onMenuOpened(): void {
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Move this block up in the list
     */
    onMoveUp(event?: PointerEvent, isToLayoutBlock: boolean = false): void {
        // this.isMoveUp = true;
        this.pageBuilderService.moveBlock(this.block, BlockMoveDirectionEnum.up, this.blockPosition, isToLayoutBlock);
        event?.stopPropagation();
    }

    /**
     * Move this block down in the list
     */
    onMoveDown(event?: PointerEvent, isToLayoutBlock: boolean = false): void {
        // this.isMoveDown = true;
        this.pageBuilderService.moveBlock(this.block, BlockMoveDirectionEnum.down, this.blockPosition, isToLayoutBlock);
        event?.stopPropagation();
    }

    onMoveLeft(event?: PointerEvent): void {
        this.pageBuilderService.moveBlock(this.block, BlockMoveDirectionEnum.left, this.blockPosition);
        event?.stopPropagation();
    }

    onMoveRight(event?: PointerEvent): void {
        this.pageBuilderService.moveBlock(this.block, BlockMoveDirectionEnum.right, this.blockPosition);
        event?.stopPropagation();
    }

    /**
     * Gets the input Block object
     *
     * @returns A BlockModel object
     */
    getBlockObject(): BlockModel {
        return this.block;
    }

    /**
     * Sets the "editing" state of a Display Element Component
     *
     * @param editing True if the component is in "editing" state, false otherwise
     */
    setEditing(editing: boolean): void {
        this.editing = editing;

        // this line stops an 'expression changed after it was checked' error from throwing
        this.changeDetectorRef.detectChanges();
    }

    setRenderedOutput(event: string): void {
        this.block.renderedOutput = event;
    }

    /**
     * Assigns a unique dynamic id to be used in the DOM.
     */
    protected assignId() {
        if (this.isInLayoutBlock || this.isInCuratorBlock) {
            this.id = 'block_' + this.blockPosition.split('.').join('_');
        } else {
            this.id = 'block_' + this.blockPosition;
        }
        this.dataBlockType = this.block.type.key;
    }

    inlineAddToggle(event: Event, element?: any, componentClass: string = 'agpr-block'): boolean {
        // container is the wrapper for both the toggle button and the adder
        let container: HTMLElement = element ? element : event.currentTarget;
        while (!container.classList.contains(componentClass + '-contextMenu')) {
            container = container.parentElement;
        }
        if (
            container.classList.contains(componentClass + '-contextMenu-adder-toTop') ||
            container.classList.contains(componentClass + '-contextMenu-adder-toBottom')
        ) {
            this.dismissContextMenu(container, componentClass);
            event.stopPropagation();
            return false;
        } else {
            // briefly show the adder to get its real height so the up-vs-down placement decision is based on actual size
            container.classList.add(componentClass + '-contextMenu-adder-toBottom');
            const adderEl = container.querySelector(
                '.' + componentClass + '-contextMenu-adder'
            ) as HTMLElement | null;
            const adderHeight = Math.max(adderEl?.offsetHeight ?? 0, this.inlineAdderHeight);
            container.classList.remove(componentClass + '-contextMenu-adder-toBottom');

            const isEnoughSpace = adderHeight <= (container.getBoundingClientRect().top - 80);

            if (isEnoughSpace) {
                container.classList.add(componentClass + '-contextMenu-adder-toTop');
            } else {
                container.classList.add(componentClass + '-contextMenu-adder-toBottom');
            }
            if (container.classList.contains(componentClass + '-contextMenu--center-top')) {
                this.addTopExpanded = true;
            } else {
                this.addBottomExpanded = true;
            }
            this.changeDetectorRef.detectChanges();

            return true;
        }
    }

    leaveContextMenu(container: HTMLElement, event?: any, mouseLeave?: boolean, componentClass: string = 'agpr-block'): void {
        if (!mouseLeave && (event instanceof FocusEvent && (event?.relatedTarget as HTMLElement)?.id?.includes(this.blockAdderId))) {
            return;
        }
        this.dismissContextMenu(container, componentClass);
    }

    dismissContextMenu(container: HTMLElement, componentClass: string = 'agpr-block'): void {
        // removing after 200 milliseconds allowing for the click to register
        setTimeout(() => {
            // removing top/bottom container
            if (container.classList.contains(componentClass + '-contextMenu-adder-toTop')){
                container.classList.remove(componentClass + '-contextMenu-adder-toTop');
            } else {
                container.classList.remove(componentClass + '-contextMenu-adder-toBottom');
            }
            if (container.classList.contains(componentClass + '-contextMenu--center-top')) {
                this.addTopExpanded = false;
            } else {
                this.addBottomExpanded = false;
            }
            this.changeDetectorRef.detectChanges();
        }, 0);
    }

    restoreFocus(moveInfo: FocusedBlockInfo): void {
        requestAnimationFrame(() => {
            if (this.blockPosition !== moveInfo?.blockPosition) {
                return;
            }

            if (this.moveButtons) {
                if (moveInfo.focusedBlockButtonType === FocusedMoveButtonTypesEnum.up) {
                    focusManagementHelper.restoreElementFocus(this.moveButtons.nativeElement, this.moveUpButtonClassName + ' > button');
                } else if (moveInfo.focusedBlockButtonType === FocusedMoveButtonTypesEnum.down) {
                    focusManagementHelper.restoreElementFocus(this.moveButtons.nativeElement, this.moveDownButtonClassName + ' > button');
                }
            }
            if (this.moveContextMenu?.buttonViewChild && !moveInfo.focusedBlockButtonType) {
                focusManagementHelper.restoreElementFocus(this.moveContextMenu.buttonViewChild.nativeElement, 'agpr-button');
            }
            this.moveInfo = null;
        });
    }

    /**
     * Checks if the block's moveability state has changed since last cached.
     * Returns true if menus need to be rebuilt.
     */
    protected hasMoveabilityChanged(): boolean {
        if (!this._cachedMoveState) {
            return true; // First time, needs to build
        }
        return (
            this._cachedMoveState.canMoveUp !== this.block.canMoveUp ||
            this._cachedMoveState.canMoveDown !== this.block.canMoveDown ||
            this._cachedMoveState.canMoveUpIntoLayout !== this.block.canMoveUpIntoLayout ||
            this._cachedMoveState.canMoveUpIntoCurator !== this.block.canMoveUpIntoCurator ||
            this._cachedMoveState.canMoveDownIntoLayout !== this.block.canMoveDownIntoLayout ||
            this._cachedMoveState.canMoveDownIntoCurator !== this.block.canMoveDownIntoCurator ||
            this._cachedMoveState.canMoveLeft !== this.block.canMoveLeft ||
            this._cachedMoveState.canMoveRight !== this.block.canMoveRight ||
            this._cachedMoveState.canDuplicate !== this.block.canDuplicate
        );
    }

    /**
     * Updates the cached moveability state after rebuilding menus.
     */
    protected updateCachedMoveState(): void {
        this._cachedMoveState = {
            canMoveUp: this.block.canMoveUp,
            canMoveDown: this.block.canMoveDown,
            canMoveUpIntoLayout: this.block.canMoveUpIntoLayout,
            canMoveUpIntoCurator: this.block.canMoveUpIntoCurator,
            canMoveDownIntoLayout: this.block.canMoveDownIntoLayout,
            canMoveDownIntoCurator: this.block.canMoveDownIntoCurator,
            canMoveLeft: this.block.canMoveLeft,
            canMoveRight: this.block.canMoveRight,
            canDuplicate: this.block.canDuplicate
        };
    }

    setContextMenuMovingOptions(): void {
        this.movingOptions = [];
        if (this.block.canMoveUp) {
            if (this.block.canMoveUpIntoLayout) {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveAboveLayout',
                        icon: 'fa-regular fa-arrow-up',
                        command: () => this.onMoveUp()
                    },
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveUpIntoLayout',
                        icon: 'fa-regular fa-arrow-up-to-bracket',
                        command: () => this.onMoveUp(null, true)
                    }
                );
            } else if (this.block.canMoveUpIntoCurator) {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveAboveCurator',
                        icon: 'fa-regular fa-arrow-up',
                        command: () => this.onMoveUp()
                    },
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveUpIntoCurator',
                        icon: 'fa-regular fa-arrow-up-to-bracket',
                        command: () => this.onMoveUp(null, true)
                    }
                );
            } else {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveUp',
                        icon: 'fa-regular fa-arrow-up',
                        command: () => this.onMoveUp()
                    }
                );
            }
        }
        if (this.block.canMoveDown) {
            if (this.block.canMoveDownIntoLayout) {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveBelowLayout',
                        icon: 'fa-regular fa-arrow-down',
                        command: () => this.onMoveDown()
                    },
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveDownIntoLayout',
                        icon: 'fa-regular fa-arrow-down-to-bracket',
                        command: () => this.onMoveDown(null, true)
                    }
                );
            } else if (this.block.canMoveDownIntoCurator) {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveBelowCurator',
                        icon: 'fa-regular fa-arrow-down',
                        command: () => this.onMoveDown()
                    },
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveDownIntoCurator',
                        icon: 'fa-regular fa-arrow-down-to-bracket',
                        command: () => this.onMoveDown(null, true)
                    }
                );
            } else {
                this.movingOptions.push(
                    {
                        label: 'PageBuilder.blockMenu.dropdown.moveDown',
                        icon: 'fa-regular fa-arrow-down',
                        command: () => this.onMoveDown()
                    }
                );
            }
        }
    }

    setContextMenuInLayoutBlockMovingOptions(): void {
        this.setContextMenuGeneralMovingOptions();
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.placeAboveLayout',
            style: {
                labelMarginLeft: '25px'
            },
            command: () => this.onMoveUp()
        });
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.placeBelowLayout',
            style: {
                labelMarginLeft: '25px'
            },
            command: () => this.onMoveDown()
        });
    }

    setContextMenuInCuratorBlockMovingOptions(): void {
        this.setContextMenuGeneralMovingOptions();

        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.placeAboveCurator',
            style: {
                labelMarginLeft: '25px'
            },
            command: () => this.onMoveUp()
        });
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.placeBelowCurator',
            style: {
                labelMarginLeft: '25px'
            },
            command: () => this.onMoveDown()
        });
    }

    setContextMenuGeneralMovingOptions(): void {
        this.inLayoutBlockMovingOptions = [];

        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.moveUp',
            disabled: !this.block.canMoveUp,
            icon: 'fa-regular fa-arrow-up',
            command: () => this.onMoveUp(null, true)
        });
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.moveDown',
            disabled: !this.block.canMoveDown,
            icon: 'fa-regular fa-arrow-down',
            command: () => this.onMoveDown(null, true)
        });
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.moveLeft',
            disabled: !this.block.canMoveLeft,
            icon: 'fa-regular fa-arrow-left',
            command: () => this.onMoveLeft()
        });
        this.inLayoutBlockMovingOptions.push({
            label: 'PageBuilder.blockMenu.dropdown.moveRight',
            disabled: !this.block.canMoveRight,
            icon: 'fa-regular fa-arrow-right',
            command: () => this.onMoveRight()
        });
        this.inLayoutBlockMovingOptions.push({
            separator: true
        });
    }

    setBlockAdderOptions(): void {
        this.optionsTop = [];
        this.optionsBottom = [];
        this.optionsCenter = [];

        const componentOptionsTop = {
            label: 'PageBuilder.inlineAdder.components',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        const componentOptionsBottom = {
            label: 'PageBuilder.inlineAdder.components',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        const layoutOptionsTop = {
            label: 'PageBuilder.inlineAdder.layouts',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        const layoutOptionsBottom = {
            label: 'PageBuilder.inlineAdder.layouts',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        if (this.blockTypeGroups?.length > 0) {
            this.blockTypeGroups.forEach(blockGroup => {
                blockGroup.blockTypes.filter(x => !!x.showInBlockAdder).forEach(blockType => {
                    if (blockType.canAdd) {
                        this.optionsCenter.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            key: blockType.key,
                            command: (menuItemEvent: CommandParams) => {
                                const position = menuItemEvent.position || this.blockPosition;
                                this.pageBuilderService.addBlockOfTypeWithEvent(blockType, position, menuItemEvent?.originalEvent);
                            }
                        });

                        componentOptionsTop.items.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            // eslint-disable-next-line max-len
                            command: (menuItemEvent) => this.pageBuilderService.addBlockOfTypeWithEvent(blockType, this.blockPosition, menuItemEvent?.originalEvent)
                        });
                        componentOptionsBottom.items.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            // eslint-disable-next-line max-len
                            command: (menuItemEvent) => this.pageBuilderService.addBlockOfTypeWithEvent(blockType, this.blockPositionBottom, menuItemEvent?.originalEvent)
                        });
                    }
                });
            });
        }

        this.pageBuilderService.getLayoutBlockTypes().pipe(take(1))
            .subscribe((layoutBlocks) => {
                layoutBlocks?.filter(x => !!x.showInBlockAdder).forEach(layout => {
                    switch (layout.key.toLowerCase()) {
                        case 'one_column_layout':
                            layout.icon = 'fa-duotone fa-regular fa-rectangle';
                            break;
                        case 'two_column_layout':
                            layout.icon = 'fa-kit-duotone fa-agpr-columns-2';
                            break;
                        case 'three_column_layout':
                            layout.icon = 'fa-duotone fa-regular fa-columns-3';
                            break;
                    }
                    if (layout.canAdd) {
                        layoutOptionsTop.items.push({
                            label: layout.label,
                            icon: layout.icon,
                            isLocked: layout.isLocked,
                            command: (menuItemEvent) => {
                                this.pageBuilderService.addBlockOfTypeWithEvent(layout, this.blockPosition, menuItemEvent?.originalEvent);
                            }
                        });
                        layoutOptionsBottom.items.push({
                            label: layout.label,
                            icon: layout.icon,
                            isLocked: layout.isLocked,
                            command: (menuItemEvent) => {
                                this.pageBuilderService.addBlockOfTypeWithEvent(layout, this.blockPositionBottom, menuItemEvent?.originalEvent);
                            }
                        });
                    }
                });
                if (componentOptionsTop.items?.length && layoutOptionsTop.items.length) {
                    this.optionsTop.push(componentOptionsTop, layoutOptionsTop);
                }
                if (componentOptionsBottom.items?.length && layoutOptionsBottom.items.length) {
                    this.optionsBottom.push(componentOptionsBottom, layoutOptionsBottom);
                }
            });
    }

    setContextMenuEditingOptions(): void {
        this.editingOptions = [];

        const addToTopOption: MenuItem = {
            label: 'PageBuilder.blockMenu.dropdown.addToTop',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };
        const addToBottomOption: MenuItem = {
            label: 'PageBuilder.blockMenu.dropdown.addToBottom',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };
        if (this.blockTypeGroups?.length > 0) {
            this.blockTypeGroups.forEach(blockGroup => {
                blockGroup.blockTypes.filter(x => !!x.showInBlockAdder).forEach(blockType => {
                    if (blockType.canAdd
                        && (
                            (!this.isInLayoutBlock && !this.isInCuratorBlock)
                            || (this.isInLayoutBlock && blockType.allowInsideLayout)
                            || (this.isInCuratorBlock && blockType.allowInsideCurator)
                        )) {

                        addToTopOption.items.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            // eslint-disable-next-line max-len
                            command: (menuItemEvent) => this.pageBuilderService.addBlockOfTypeWithEvent(blockType, this.blockPosition, menuItemEvent?.originalEvent)
                        });
                        addToBottomOption.items.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            // eslint-disable-next-line max-len
                            command: (menuItemEvent) => this.pageBuilderService.addBlockOfTypeWithEvent(blockType, this.blockPositionBottom, menuItemEvent?.originalEvent)
                        });
                    }
                });
            });
        }

        if (addToTopOption.items?.length) {
            this.editingOptions.push(addToTopOption, addToBottomOption);
            this.editingOptions.push({
                separator: true
            });
        }

        if (this.block.canDuplicate) {
            this.editingOptions.push({
                label: 'PageBuilder.blockMenu.dropdown.duplicate',
                command: () => this.onAction('duplicate')
            });
        }
        if (this.block.canEdit) {
            this.editingOptions.push({
                label: 'PageBuilder.blockMenu.dropdown.edit',
                command: () => this.onAction('edit')
            });
        }
        if (this.block.canAddOrRemove) {
            this.editingOptions.push({
                label: 'PageBuilder.blockMenu.dropdown.delete',
                command: () => this.onAction('delete')
            });
        }
    }
}
